// src/utils/CodifyQueue.js
export default class CodifyQueue {
  constructor({
    concurrency = 1,
    maxRetries = 2,
    baseDelayMs = 700,
    jitterMs = 300,
  } = {}) {
    this.concurrency = Math.max(1, concurrency);
    this.maxRetries = maxRetries;
    this.baseDelayMs = baseDelayMs;
    this.jitterMs = jitterMs;

    this.queue = [];            // [{id,key,run,retries,status,cancelled}]
    this.running = 0;
    this.keyLatest = new Map(); // key -> last enqueued task id
    this._id = 0;

    // Track completed/failed tasks for monitoring
    this.completed = [];        // [{id,key,status,completedAt}]
    this.failed = [];           // [{id,key,error,failedAt,retries}]
    this.maxHistory = 50;       // Keep last 50 completed/failed
  }

  _nextId() {
    this._id += 1;
    return `task_${this._id}`;
  }

  get size() {
    return this.queue.filter(t => t.status === "queued").length + this.running;
  }

  // Get detailed statistics for monitoring UI
  getStats() {
    return {
      pending: this.queue.filter(t => t.status === "queued" && !t.cancelled).length,
      running: this.running,
      completed: this.completed.length,
      failed: this.failed.length,
      total: this.queue.filter(t => t.status === "queued" && !t.cancelled).length + this.running,
    };
  }

  // Get list of currently queued/running tasks
  getCurrentTasks() {
    return [
      ...this.queue.filter(t => t.status === "queued" && !t.cancelled).map(t => ({
        id: t.id,
        key: t.key,
        status: 'queued',
        retries: t.retries,
      })),
      ...this.queue.filter(t => t.status === "running").map(t => ({
        id: t.id,
        key: t.key,
        status: 'running',
        retries: t.retries,
      })),
    ];
  }

  // Get recent failures for retry UI
  getFailedTasks() {
    return [...this.failed];
  }

  // Clear completed/failed history
  clearHistory() {
    this.completed = [];
    this.failed = [];
  }

  /** Enqueue a task keyed by 'key' (e.g., rowId). Any queued (not started) older task for that key is cancelled. */
  enqueue(key, run) {
    const id = this._nextId();

    // cancel any *queued* older tasks for the same key
    for (const t of this.queue) {
      if (t.key === key && t.status === "queued") {
        t.cancelled = true;
        t.status = "cancelled";
      }
    }
    this.keyLatest.set(key, id);

    let resolve, reject;
    const promise = new Promise((res, rej) => ((resolve = res), (reject = rej)));

    const task = {
      id,
      key,
      run,                  // async (signal) => any
      retries: 0,
      status: "queued",     // queued|running|done|error|cancelled
      cancelled: false,
      resolve,
      reject,
    };

    this.queue.push(task);
    this._pump();
    return { id, promise, cancel: () => this._cancel(id) };
  }

  _cancel(id) {
    const t = this.queue.find(x => x.id === id);
    if (t && t.status === "queued") {
      t.cancelled = true;
      t.status = "cancelled";
    }
  }

  async _pump() {
    while (this.running < this.concurrency) {
      // pick next queued + not cancelled
      const idx = this.queue.findIndex(t => t.status === "queued" && !t.cancelled);
      if (idx === -1) return;

      const task = this.queue[idx];
      task.status = "running";
      this.running += 1;

      // Remove from array now; we'll manage completion separately
      this.queue.splice(idx, 1);

      // Fire & forget (but await inside try/finally to control running count)
      this._runTask(task)
        .catch(() => {}) // error handled inside
        .finally(() => {
          this.running -= 1;
          // continue processing
          this._pump();
        });
    }
  }

  async _runTask(task) {
    // If a newer task for this key exists, skip this run entirely
    if (this.keyLatest.get(task.key) !== task.id) {
      task.status = "cancelled";
      task.resolve({ skipped: true });
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const result = await task.run(signal);
      task.status = "done";

      // Track successful completion
      this.completed.push({
        id: task.id,
        key: task.key,
        status: 'done',
        completedAt: new Date().toISOString(),
      });
      if (this.completed.length > this.maxHistory) {
        this.completed.shift(); // Remove oldest
      }

      task.resolve(result);
    } catch (err) {
      if (err?.name === "AbortError" || task.cancelled) {
        task.status = "cancelled";
        task.resolve({ aborted: true });
        return;
      }

      // decide retry
      const retriable =
        err?.retriable === true ||
        (typeof err?.status === "number" && (err.status === 429 || err.status >= 500));

      if (retriable && task.retries < this.maxRetries) {
        task.retries += 1;
        task.status = "queued";
        // jittered backoff
        const delay =
          this.baseDelayMs * Math.pow(2, task.retries - 1) +
          Math.floor(Math.random() * this.jitterMs);
        await new Promise(r => setTimeout(r, delay));

        // If superseded while waiting, treat as cancelled
        if (this.keyLatest.get(task.key) !== task.id || task.cancelled) {
          task.status = "cancelled";
          task.resolve({ skipped: true });
          return;
        }

        // Re-queue at tail and pump
        this.queue.push(task);
        this._pump();
        return;
      }

      // Track failure
      task.status = "error";
      this.failed.push({
        id: task.id,
        key: task.key,
        error: err.message || 'Unknown error',
        failedAt: new Date().toISOString(),
        retries: task.retries,
      });
      if (this.failed.length > this.maxHistory) {
        this.failed.shift(); // Remove oldest
      }

      task.reject(err);
    }
  }
}
